import { useEffect, useState } from 'react';
import { ArrowUpRight, Percent } from 'lucide-react';
import { toast } from 'sonner';
import { api, eur } from '../../lib/project';
import { monthsToShort } from '../../lib/api';
import { requestView } from '../../lib/viewBridge';

/**
 * Compact strip that surfaces the dossier scenarios inside the pitch view.
 * Each label uses the color configured in the dossier so the two vistas stay
 * visually consistent. Clicking a card jumps the visitor straight into the
 * dossier with the scenario preselected.
 */
export function PitchScenarioLabels() {
  const [data, setData] = useState(null);
  const [applyDiscount, setApplyDiscount] = useState(false);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    api.get('/acquisition/all', { params: { apply_discount: applyDiscount } })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { /* silent — pitch stays usable without the dossier */ })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [applyDiscount]);

  const scenarios = data?.scenarios || {};
  const keys = Object.keys(scenarios);
  if (!busy && keys.length === 0) return null;

  const goToDossier = (key, label) => {
    requestView('dossier', { scenarioKey: key, label: `Cenário ${label} do dossier` });
    toast.success('A abrir Planeamento com o cenário selecionado.');
  };

  return (
    <section className="pitch-scenario-labels" data-testid="pitch-scenario-labels">
      <div className="pitch-scenario-labels-header">
        <div>
          <span className="section-mini-label">CENÁRIOS DO PLANEAMENTO</span>
          <h3>Os cenários avaliados no dossier de aquisição.</h3>
          <p>Fonte única de dados; edite descontos, cores e vendas no Planeamento e o pitch atualiza automaticamente.</p>
        </div>
        <label className="pitch-scenario-discount-toggle" data-testid="pitch-labels-toggle-discount">
          <input type="checkbox" checked={applyDiscount} onChange={(e) => setApplyDiscount(e.target.checked)} />
          <span><Percent size={11} /> Com desconto</span>
        </label>
      </div>
      <div className="pitch-scenario-cards" role="list">
        {keys.map((key) => {
          const sc = scenarios[key];
          const color = sc.color || '#334155';
          const y1 = sc.years?.[0];
          return (
            <button
              key={key}
              type="button"
              role="listitem"
              className="pitch-scenario-card"
              onClick={() => goToDossier(key, sc.label)}
              data-testid={`pitch-scenario-${key}`}
              style={{ borderTopColor: color }}
            >
              <div className="pitch-scenario-card-head">
                <span className="pitch-scenario-dot" style={{ background: color }} aria-hidden="true" />
                <strong>{sc.label}</strong>
              </div>
              {sc.description && <p className="pitch-scenario-desc">{sc.description}</p>}
              <div className="pitch-scenario-metrics">
                <div>
                  <span>Unidades ano 1</span>
                  <b>{y1?.units ?? '—'}</b>
                </div>
                <div>
                  <span>EBITDA ano 1</span>
                  <b>{y1 ? eur(y1.ebitda, 0) : '—'}</b>
                </div>
                <div>
                  <span>Payback</span>
                  <b>{monthsToShort(sc.payback_total_capital_months)}</b>
                </div>
              </div>
              <span className="pitch-scenario-cta">Ver no Planeamento <ArrowUpRight size={13} /></span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
